import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, money, formatApiErrorDetail } from "../lib/api";
import PageHeader from "../components/PageHeader";
import SearchableSelect from "../components/SearchableSelect";

const STEPS = [
  "Pick the machine",
  "Pick the customer",
  "Dates & rate",
  "Extras",
  "Have a look & confirm",
];

const BILLING_STYLES = [
  { value: "standard-monthly", label: "Standard monthly (weekly × 4.33)" },
  { value: "weekly", label: "Weekly" },
  { value: "custom-period", label: "Custom period (choose days)" },
  { value: "tier-one", label: "Tier-one (set amount each month)" },
];

const DEFAULT_FORM = {
  machine_id: "",
  customer_id: "",
  start_date: new Date().toISOString().slice(0, 10),
  expected_end_date: "",
  continuous_hire: false,
  weekly_rate: 2400,
  billing_style: "standard-monthly",
  tier_one_amount: "",
  custom_period_days: 17,
  custom_period_amount: "",
  site_location: "",
  notes: "",
  po_number: "",
  po_expiry_date: "",
  transport_in: "",
  transport_out: "",
  transport_provider: "",
  custom_charges: [], // [{description, rate, qty, kind}]
};

export default function StartHire() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState(1);
  const [machines, setMachines] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [f, setF] = useState(DEFAULT_FORM);
  const [loadedDraftId, setLoadedDraftId] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/machines/available/for-hire"),
      api.get("/customers"),
    ]).then(([m, c]) => {
      setMachines(m.data);
      setCustomers(c.data);
      // Try loading a specific draft from ?draft=<id>
      const draftId = searchParams.get("draft");
      if (draftId) {
        api.get(`/drafts/${draftId}`).then(r => {
          setF({ ...DEFAULT_FORM, ...(r.data.payload || {}) });
          setLoadedDraftId(draftId);
        }).catch(()=>{});
        return;
      }
      // Or prefill from a converted quote (existing behaviour)
      try {
        const raw = sessionStorage.getItem("quote-prefill");
        if (raw) {
          const p = JSON.parse(raw);
          setF(prev => ({
            ...prev,
            machine_id: p.machine_id || prev.machine_id,
            customer_id: p.customer_id || prev.customer_id,
            weekly_rate: p.weekly_rate || prev.weekly_rate,
            site_location: p.site_location || prev.site_location,
          }));
          if (p.quote_id) sessionStorage.setItem("quote-prefill-quote-id", p.quote_id);
          sessionStorage.removeItem("quote-prefill");
        }
      } catch (e) { /* ignore */ }
    });
  }, [searchParams]);

  const selMachine = machines.find(m => m.id === f.machine_id);
  const selCustomer = customers.find(c => c.id === f.customer_id);

  const monthly = useMemo(() => {
    const w = Number(f.weekly_rate || 0);
    if (f.billing_style === "tier-one") return Number(f.tier_one_amount || 0);
    if (f.billing_style === "weekly") return Math.round((w * 30.44 / 7) * 100) / 100;
    if (f.billing_style === "custom-period") {
      if (f.custom_period_amount && Number(f.custom_period_amount) > 0) {
        return Number(f.custom_period_amount);
      }
      const days = Number(f.custom_period_days || 0);
      if (!days) return 0;
      const daily = (w * 4.33) / 30.44;
      return Math.round(daily * days * 100) / 100;
    }
    return Math.round(w * 4.33 * 100) / 100;
  }, [f.weekly_rate, f.billing_style, f.tier_one_amount, f.custom_period_days, f.custom_period_amount]);
  const monthlyLabel = useMemo(() => {
    if (f.billing_style === "tier-one") return "tier-one flat amount";
    if (f.billing_style === "weekly") return `${money(f.weekly_rate)} × ≈4.35 wks/mo`;
    if (f.billing_style === "custom-period") {
      if (f.custom_period_amount && Number(f.custom_period_amount) > 0) {
        return `flat amount per ${f.custom_period_days || 0}-day period`;
      }
      return `pro-rata over ${f.custom_period_days || 0} days`;
    }
    return `${money(f.weekly_rate)} × 4.33`;
  }, [f.weekly_rate, f.billing_style, f.custom_period_days, f.custom_period_amount]);

  const canNext = () => {
    if (step === 1) return !!f.machine_id;
    if (step === 2) return !!f.customer_id;
    if (step === 3) return f.start_date && f.weekly_rate > 0 && (f.continuous_hire || f.expected_end_date);
    return true;
  };

  const setCharge = (i, k, v) => {
    const cs = f.custom_charges.slice();
    cs[i] = { ...cs[i], [k]: v };
    setF({ ...f, custom_charges: cs });
  };
  const addCharge = () => setF({ ...f, custom_charges: [...f.custom_charges, { description: "", rate: "", qty: 1, kind: "weekly" }] });
  const removeCharge = (i) => setF({ ...f, custom_charges: f.custom_charges.filter((_, idx) => idx !== i) });

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const payload = { ...f, weekly_rate: Number(f.weekly_rate) };
      if (payload.billing_style !== "tier-one") delete payload.tier_one_amount;
      else payload.tier_one_amount = Number(payload.tier_one_amount || monthly);
      if (payload.billing_style !== "custom-period") { delete payload.custom_period_days; delete payload.custom_period_amount; }
      else {
        if (payload.custom_period_amount === "" || payload.custom_period_amount == null) delete payload.custom_period_amount;
        else payload.custom_period_amount = Number(payload.custom_period_amount);
      }
      if (payload.continuous_hire || !payload.expected_end_date) delete payload.expected_end_date;
      if (!payload.transport_in) delete payload.transport_in; else payload.transport_in = Number(payload.transport_in);
      if (!payload.transport_out) delete payload.transport_out; else payload.transport_out = Number(payload.transport_out);
      if (!payload.transport_provider) delete payload.transport_provider;
      if (!payload.po_number) delete payload.po_number;
      if (!payload.po_expiry_date) delete payload.po_expiry_date;
      payload.custom_charges = (payload.custom_charges || [])
        .filter(c => (c.description || "").trim() && Number(c.rate) > 0)
        .map(c => ({
          description: c.description.trim(),
          rate: Number(c.rate),
          qty: Number(c.qty || 1),
          kind: (c.kind === "one-off" ? "one-off" : "weekly"),
        }));
      const prefillRaw = sessionStorage.getItem("quote-prefill-quote-id");
      if (prefillRaw) payload.quote_id = prefillRaw;
      const r = await api.post("/hires", payload);
      sessionStorage.removeItem("quote-prefill-quote-id");
      if (loadedDraftId) { try { await api.delete(`/drafts/${loadedDraftId}`); } catch (e) {} }
      setBusy(false);
      nav("/dashboard");
      return r;
    } catch (e) {
      setBusy(false);
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const saveDraft = async () => {
    setBusy(true); setErr("");
    try {
      const title = selMachine && selCustomer
        ? `${selMachine.unit_number} → ${selCustomer.legal_name}`
        : selMachine ? `${selMachine.unit_number} (no customer yet)`
        : selCustomer ? `${selCustomer.legal_name} (no machine yet)`
        : "Untitled hire draft";
      if (loadedDraftId) { try { await api.delete(`/drafts/${loadedDraftId}`); } catch (e) {} }
      await api.post("/drafts", { kind: "hire", title, payload: f });
      setBusy(false);
      nav("/dashboard");
    } catch (e) {
      setBusy(false);
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div data-testid="start-hire-page">
      <PageHeader
        title="Start a hire"
        subtitle={loadedDraftId ? "Resuming a saved draft" : "Five steps — nothing to remember"}
      />

      <div className="ach-card" style={{marginBottom: 12}}>
        <h2 className="ach-card__title">Five steps — nothing to remember</h2>
        <div className="ach-steps" data-testid="wizard-steps">
          {STEPS.map((s, i) => {
            const n = i + 1;
            const cls = n < step ? "done" : (n === step ? "current" : "");
            return <div key={s} className={`ach-step ${cls}`} data-testid={`step-chip-${n}`}>{n}. {s}</div>;
          })}
        </div>
      </div>

      <div className="ach-card" data-testid="wizard-body">
        <h2 className="ach-card__title">Step {step} — {STEPS[step-1]}</h2>

        {step === 1 && (
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Which machine are we sending out?</label>
              <SearchableSelect
                testid="pick-machine"
                value={f.machine_id}
                onChange={(v)=>setF({...f, machine_id: v})}
                placeholder="— pick one from the yard —"
                options={machines.map(m => ({
                  value: m.id,
                  label: `${m.unit_number} · ${m.make} ${m.model_name} · ${m.capacity_tonnes}t`,
                  sub: `Serial ${m.serial || "—"} · ${m.owning_company}`,
                }))}
              />
              <div className="helper">Only machines that are free show here. Type to filter by unit, model, capacity, or serial.</div>
            </div>
            {selMachine && (
              <div className="ach-explainer" data-testid="pick-machine-summary">
                Picked <strong>{selMachine.unit_number}</strong> — {selMachine.make} {selMachine.model_name} ({selMachine.capacity_tonnes}t). Owned by <strong>{selMachine.owning_company}</strong>. Currently in {selMachine.location}.
              </div>
            )}
          </div>
        )}

        {step === 2 && (
          <div className="ach-form-grid">
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Who&apos;s hiring it?</label>
              <SearchableSelect
                testid="pick-customer"
                value={f.customer_id}
                onChange={(v)=>setF({...f, customer_id: v})}
                placeholder="— pick a customer —"
                options={customers.filter(c => !c.is_prospect).map(c => ({
                  value: c.id,
                  label: c.legal_name,
                  sub: `${c.account_status.replace(/-/g,' ')}${c.abn ? ' · ABN ' + c.abn : ''}`,
                }))}
              />
              <div className="helper">Prospects (no ABN yet) don&apos;t show here — quote them first, then finish setup before starting the hire.</div>
            </div>
            {selCustomer && (
              <div className="ach-explainer" data-testid="pick-customer-summary">
                Hiring to <strong>{selCustomer.legal_name}</strong>. ABN {selCustomer.abn}. Account is <strong>{selCustomer.account_status.replace(/-/g, " ")}</strong>.
                {selCustomer.owing_amount > 0 && ` They currently owe ${money(selCustomer.owing_amount)}.`}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div className="ach-form-grid">
            <div className="ach-field">
              <label>Start date</label>
              <input data-testid="start-date" type="date" value={f.start_date} onChange={(e)=>setF({...f, start_date: e.target.value})} />
            </div>
            <div className="ach-field">
              <label>Expected off-hire date</label>
              <input data-testid="expected-end-date" type="date" value={f.expected_end_date} onChange={(e)=>setF({...f, expected_end_date: e.target.value})} disabled={f.continuous_hire} />
              <label style={{display:"flex", alignItems:"center", gap:8, marginTop:6, fontSize:12, color:"var(--ach-text-1)", fontWeight:500}}>
                <input type="checkbox" checked={f.continuous_hire} onChange={(e)=>setF({...f, continuous_hire: e.target.checked, expected_end_date: e.target.checked ? "" : f.expected_end_date})} data-testid="continuous-hire" />
                No fixed end / continuous hire
              </label>
            </div>
            <div className="ach-field">
              <label>Weekly rate ($)</label>
              <input data-testid="weekly-rate" type="number" min="0" step="50" value={f.weekly_rate}
                onChange={(e)=>setF({...f, weekly_rate: e.target.value})} />
              <div className="helper">Monthly charge worked out for you: <strong data-testid="monthly-worked-out">{money(monthly)} + GST</strong> ({monthlyLabel})</div>
            </div>
            <div className="ach-field">
              <label>How it's billed</label>
              <select data-testid="billing-style" value={f.billing_style} onChange={(e)=>setF({...f, billing_style: e.target.value})}>
                {BILLING_STYLES.map(b => <option key={b.value} value={b.value}>{b.label}</option>)}
              </select>
            </div>
            {f.billing_style === "tier-one" && (
              <div className="ach-field">
                <label>Tier-one monthly amount ($)</label>
                <input data-testid="tier-one-amount" type="number" value={f.tier_one_amount} onChange={(e)=>setF({...f, tier_one_amount: e.target.value})} />
              </div>
            )}
            {f.billing_style === "custom-period" && (
              <>
                <div className="ach-field">
                  <label>Custom period (days)</label>
                  <input data-testid="custom-days" type="number" value={f.custom_period_days} onChange={(e)=>setF({...f, custom_period_days: e.target.value})} />
                </div>
                <div className="ach-field">
                  <label>Custom period flat amount ($)</label>
                  <input data-testid="custom-period-amount" type="number" min="0" value={f.custom_period_amount} onChange={(e)=>setF({...f, custom_period_amount: e.target.value})} placeholder="Leave blank to use weekly × days" />
                  <div className="helper">Optional — fill this in to bill a flat dollar amount per period instead of back-calculating from the weekly rate.</div>
                </div>
              </>
            )}
            <div className="ach-field" style={{gridColumn:"1 / -1"}}>
              <label>Site / location</label>
              <input data-testid="site-location" type="text" value={f.site_location} onChange={(e)=>setF({...f, site_location: e.target.value})} placeholder="e.g. Boral quarry — Wingham" />
            </div>
            <div className="ach-field">
              <label>Customer PO number</label>
              <input data-testid="po-number" value={f.po_number} onChange={(e)=>setF({...f, po_number: e.target.value})} placeholder="e.g. PO-45129" />
            </div>
            <div className="ach-field">
              <label>PO expiry date</label>
              <input data-testid="po-expiry-date" type="date" value={f.po_expiry_date} onChange={(e)=>setF({...f, po_expiry_date: e.target.value})} />
              <div className="helper">Optional — leave blank if the PO has no expiry. The dashboard flags any job where it's within 30 days.</div>
            </div>
          </div>
        )}

        {step === 4 && (
          <div>
            <div className="ach-form-grid">
              <div className="ach-field">
                <label>Transport in — one-off ($)</label>
                <input type="number" min="0" value={f.transport_in} onChange={(e)=>setF({...f, transport_in: e.target.value})} data-testid="transport-in" placeholder="0" />
              </div>
              <div className="ach-field">
                <label>Transport out — one-off ($)</label>
                <input type="number" min="0" value={f.transport_out} onChange={(e)=>setF({...f, transport_out: e.target.value})} data-testid="transport-out" placeholder="0" />
                <div className="helper" data-testid="transport-out-defer-note">Billed in the month the hire ends — reconfirmed at off-hire per your conditions clause (c).</div>
              </div>
              <div className="ach-field" style={{gridColumn:"1 / -1"}}>
                <label>Transport provider (who was used / quoted)</label>
                <input value={f.transport_provider} onChange={(e)=>setF({...f, transport_provider: e.target.value})} data-testid="transport-provider" placeholder="e.g. AJP Heavy Haulage" />
                <div className="helper">Free text — recorded on the hire so it can be found again later.</div>
              </div>
            </div>

            <div style={{marginTop:16}}>
              <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
                <h3 style={{fontSize:14, fontWeight:600, margin:0}}>Extra charge lines</h3>
                <button className="ach-btn ach-btn--grey" style={{padding:"4px 10px", fontSize:11}} onClick={addCharge} data-testid="add-custom-charge">+ Add charge line</button>
              </div>
              <div className="helper" style={{marginBottom:8}}>Weekly lines (e.g. <em>Chains @ $80/wk</em>) bill every month at rate × 4.33, same maths as the main hire. One-off lines bill the flat amount once.</div>
              {f.custom_charges.length === 0 && (
                <div style={{padding:12, background:"var(--ach-page-bg)", borderRadius:6, fontSize:12, color:"var(--ach-text-2)"}}>No extra charges yet.</div>
              )}
              {f.custom_charges.map((c, i) => (
                <div key={i} className="ach-form-grid" style={{gridTemplateColumns:"2fr 1fr 1fr 1.1fr auto", gap:8, marginBottom:8, alignItems:"end"}} data-testid={`custom-charge-${i}`}>
                  <div className="ach-field"><label>{i === 0 ? "Description" : ""}</label>
                    <input value={c.description} onChange={(e)=>setCharge(i, "description", e.target.value)} placeholder="e.g. Lifting gear" data-testid={`charge-${i}-desc`} />
                  </div>
                  <div className="ach-field"><label>{i === 0 ? "Rate ($)" : ""}</label>
                    <input type="number" min="0" value={c.rate} onChange={(e)=>setCharge(i, "rate", e.target.value)} data-testid={`charge-${i}-rate`} />
                  </div>
                  <div className="ach-field"><label>{i === 0 ? "Qty" : ""}</label>
                    <input type="number" min="1" value={c.qty} onChange={(e)=>setCharge(i, "qty", e.target.value)} data-testid={`charge-${i}-qty`} />
                  </div>
                  <div className="ach-field"><label>{i === 0 ? "Type" : ""}</label>
                    <select value={c.kind || "weekly"} onChange={(e)=>setCharge(i, "kind", e.target.value)} data-testid={`charge-${i}-kind`}>
                      <option value="weekly">Weekly (× 4.33/mo)</option>
                      <option value="one-off">One-off (once)</option>
                    </select>
                  </div>
                  <button className="ach-btn ach-btn--ghost" style={{padding:"6px 8px", fontSize:11}} onClick={()=>removeCharge(i)} data-testid={`charge-${i}-remove`}>Remove</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {step === 5 && (
          <div>
            <table className="ach-table" style={{marginBottom: 12}} data-testid="review-table">
              <tbody>
                <tr><td style={{width: 200, color: "var(--ach-text-2)"}}>Machine</td><td><strong>{selMachine?.unit_number}</strong> · {selMachine?.make} {selMachine?.model_name} · {selMachine?.capacity_tonnes}t · owned by {selMachine?.owning_company}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Customer</td><td>{selCustomer?.legal_name} · ABN {selCustomer?.abn}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Site</td><td>{f.site_location || "—"}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Customer PO</td><td>{f.po_number || "—"}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Starts</td><td>{f.start_date}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Off-hire expected</td><td>{f.continuous_hire ? <em>continuous — no end date</em> : (f.expected_end_date || "not set")}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Weekly rate</td><td>{money(f.weekly_rate)}</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>Monthly (worked out)</td><td><strong>{money(monthly)} + GST</strong> ({money(f.weekly_rate)} × 4.33)</td></tr>
                <tr><td style={{color: "var(--ach-text-2)"}}>How it's billed</td><td>{BILLING_STYLES.find(b => b.value === f.billing_style)?.label}</td></tr>
                {f.transport_in && <tr><td style={{color:"var(--ach-text-2)"}}>Transport in (one-off)</td><td>{money(f.transport_in)}</td></tr>}
                {f.transport_out && <tr><td style={{color:"var(--ach-text-2)"}}>Transport out (one-off)</td><td>{money(f.transport_out)}</td></tr>}
                {f.custom_charges.filter(c=>c.description && Number(c.rate)>0).length > 0 && (
                  <tr><td style={{color:"var(--ach-text-2)"}}>Extra monthly charges</td><td>{f.custom_charges.filter(c=>c.description && Number(c.rate)>0).map((c,i) => (
                    <div key={i}>{c.description} · {money(c.rate)} × {c.qty}</div>
                  ))}</td></tr>
                )}
              </tbody>
            </table>
            <div className="ach-explainer" style={{marginBottom: 12}}>
              <strong>When you press confirm</strong>: billing starts from the start date, this hire appears on the dashboard and in month-end, and any extra charges above are added to every month-end automatically.
              {err && <div style={{color:"var(--ach-red)", marginTop:8}}>{err}</div>}
            </div>
            <div style={{textAlign:"center"}}>
              <button className="ach-btn ach-btn--primary" onClick={submit} disabled={busy} data-testid="confirm-hire" style={{padding:"14px 40px", fontSize: 14}}>
                {busy ? "Starting…" : "Confirm — start this hire"}
              </button>
            </div>
          </div>
        )}

        <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginTop: 20}}>
          <button className="ach-btn ach-btn--grey" onClick={()=>step > 1 ? setStep(step - 1) : nav("/dashboard")} data-testid="wizard-back">
            {step > 1 ? "← Back" : "Cancel"}
          </button>
          <div style={{display:"flex", gap:8}}>
            <button className="ach-btn ach-btn--grey" onClick={saveDraft} disabled={busy} data-testid="wizard-save-for-later">
              {loadedDraftId ? "Update draft" : "Save draft for later"}
            </button>
            {step < 5 ? (
              <button className="ach-btn ach-btn--primary" disabled={!canNext()} onClick={()=>setStep(step + 1)} data-testid="wizard-next">Next →</button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
